/** Joriy kurs (o'rganilayotgan til) va so'z bazasi. Demo bilan bir xil: kurs localStorage'da, almashtirish — sahifa qayta yuklanadi. */
import type { Course, Word } from './types';
import { API_URL } from '../lib/api';
import { WORDS_EN } from './words.en';
import { WORDS_RU } from './words.ru';

export const COURSE_KEY = 'lexo-v2-lang';
export function readCourse(): Course {
  try { return localStorage.getItem(COURSE_KEY) === 'ru' ? 'ru' : 'en'; } catch { return 'en'; }
}
export const LANG: Course = readCourse();

export const LN = LANG === 'ru'
  ? { code: 'RU', name: 'Rus tili', Adj: 'Ruscha', adj: 'ruscha', short: 'rus', tts: 'ru-RU', vre: /^ru/i }
  : { code: 'EN', name: 'Ingliz tili', Adj: 'Inglizcha', adj: 'inglizcha', short: 'ingliz', tts: 'en-US', vre: /^en(-|_)US/i };

/** CEFR darajalari (serverdagi `words.cefr_level`). */
/** C2 yo'q: so'z bazasi (EN 5 078, RU 4 920) A1–C1. */
export const LEVEL_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1'] as const;

/**
 * So'z bazasi — serverdagi nashr qilingan so'zlar (`/v1/courses/{kurs}/words`, `position` tartibida).
 * Ishga tushganda oxirgi yuklangan nusxa (localStorage) yoki ilova ichidagi 100 ta so'z bilan to'ladi,
 * `loadWords()` server ro'yxatini shu massivlarning o'ziga yozadi (importlar o'zgarmaydi).
 */
export const W: Word[] = [];
export const BYID: Record<number, Word> = {};
export const TOPICS: string[] = [];
/** Bazadagi so'zlar soni darajalar bo'yicha (0 — hali nashr qilinmagan). */
export const LEVEL_N: Record<string, number> = {};

function setWords(ws: Word[]) {
  W.length = 0; W.push(...ws);
  for (const k of Object.keys(BYID)) delete BYID[+k];
  for (const w of W) BYID[w.id] = w;
  TOPICS.length = 0; TOPICS.push(...new Set(W.map((w) => w.topic).filter(Boolean)));
  for (const l of LEVEL_ORDER) LEVEL_N[l] = 0;
  for (const w of W) LEVEL_N[w.level] = (LEVEL_N[w.level] ?? 0) + 1;
}

const WORDS_KEY = `lexo-words-${LANG}`;
const COURSE_ID = LANG === 'ru' ? 'ru-uz' : 'en-uz';
setWords((() => {
  try { const c = JSON.parse(localStorage.getItem(WORDS_KEY) ?? 'null') as Word[] | null; if (c?.length) return c; } catch { /* ignore */ }
  return (LANG === 'ru' ? WORDS_RU : WORDS_EN).map((w) => ({ ...w, level: 'A1' }));
})());

let wordsP: Promise<boolean> | null = null;
/** Serverdan so'zlar ro'yxati (bir marta; xato bo'lsa keyingi chaqiruvda qayta urinadi). true — yangilandi. */
export function loadWords(): Promise<boolean> {
  wordsP ??= fetch(`${API_URL}/v1/courses/${COURSE_ID}/words`)
    .then((r) => (r.ok ? (r.json() as Promise<{ words: Word[] }>) : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((d) => {
      if (!d.words?.length) return false;
      setWords(d.words);
      try { localStorage.setItem(WORDS_KEY, JSON.stringify(d.words)); } catch { /* kvota — keyingi safar serverdan */ }
      return true;
    })
    .catch((e: unknown) => { console.warn('[words]', e instanceof Error ? e.message : e); wordsP = null; return false; });
  return wordsP;
}
export const wordById = (id: number): Word | undefined => BYID[id];

export const POS: Record<string, string> = {
  noun: 'ot', verb: 'feʼl', adjective: 'sifat', adverb: 'ravish', pronoun: 'olmosh', preposition: 'predlog', conjunction: 'bogʻlovchi',
  particle: 'yuklama', interjection: 'undov', numeral: 'son', determiner: 'aniqlovchi', 'modal verb': 'modal feʼl', predicative: 'predikativ',
};
/** Leitner intervallari (kun), indeks = quti (0..6). */
export const INTERVALS = [0, 1, 3, 7, 16, 35, 90] as const;
export const DAYS = ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya'] as const;

export const COURSES: Array<{ k: Course; code: string; name: string; desc: string }> = [
  { k: 'en', code: 'EN', name: 'Ingliz tili', desc: 'Ingliz → oʻzbek' },
  { k: 'ru', code: 'RU', name: 'Rus tili', desc: 'Rus → oʻzbek' },
];

/* ---------- so'z matni yordamchilari ---------- */
export const fold = (s: string) => s.toLowerCase().replace(/ё/g, 'е').replace(/́/g, '');
export const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е').replace(/́/g, '').trim().replace(/^to\s+/, '').replace(/[.!?ʼ’']/g, '').replace(/\s+/g, ' ');
const stem = (w: Word) => { const s = w.w; if (LANG !== 'ru') return s; return s.length >= 6 ? s.slice(0, -2) : s.length >= 4 ? s.slice(0, -1) : s; };
/** Misol gapdagi so'zni topish (rus tilida o'zak bo'yicha). */
export const wordRe = (w: Word) => new RegExp('(?<![\\p{L}])(' + stem(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\p{L}*)', 'iu');
export const gapOk = (w: Word) => wordRe(w).test(w.ex);

export function lev(a: string, b: string): number {
  const m = [...Array(b.length + 1).keys()];
  for (let i = 1; i <= a.length; i++) {
    let p = m[0]!; m[0] = i;
    for (let j = 1; j <= b.length; j++) { const tmp = m[j]!; m[j] = Math.min(m[j]! + 1, m[j - 1]! + 1, p + (a[i - 1] === b[j - 1] ? 0 : 1)); p = tmp; }
  }
  return m[b.length]!;
}

export function shuffle<T>(a: readonly T[]): T[] {
  const c = a.slice();
  for (let i = c.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [c[i], c[j]] = [c[j]!, c[i]!]; }
  return c;
}

/** n ta chalg'ituvchi: avval shu turkumdagi, keyin boshqa so'zlar; `key` bo'yicha takrorlanmaydi. */
export function distract(w: Word, key: 'uz' | 'w', n = 3): Word[] {
  const same = shuffle(W.filter((x) => x.id !== w.id && x.pos === w.pos && x[key] !== w[key]));
  const other = shuffle(W.filter((x) => x.id !== w.id && x.pos !== w.pos && x[key] !== w[key]));
  const out: Word[] = [];
  for (const x of same.concat(other)) { if (!out.some((o) => o[key] === x[key])) out.push(x); if (out.length === n) break; }
  return out;
}

export const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
export const esc = (s: unknown) => String(s);
